import type { ReactNode } from 'react';

import type { RealtimeConnectionState } from '@ngsos/portal-core/portal';

import { PORTAL_HUB_CLIENT_EVENTS, PORTAL_HUB_SERVER_METHODS } from '@/signalr';
import type { PortalHubConnection } from '@/signalr';

export type DevHubConnectionPanelProps = {
  hub: PortalHubConnection | null;
  connection: RealtimeConnectionState;
};

const STATUS_BADGE: Record<RealtimeConnectionState['status'], string> = {
  'never-connected': 'text-bg-warning',
  connected: 'text-bg-success',
  disconnected: 'text-bg-danger',
};

function formatElapsed(since: Date): string {
  const seconds = Math.max(0, Math.round((Date.now() - since.getTime()) / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }

  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="d-flex gap-2 border-bottom py-1">
      <div className="text-body-secondary" style={{ minWidth: '13rem' }}>
        {label}
      </div>
      <div className="font-monospace text-break">{children}</div>
    </div>
  );
}

function ContractTable({ title, contracts }: { title: string; contracts: Record<string, { params: readonly string[]; returns: string }> }) {
  return (
    <details className="mt-2">
      <summary className="small text-body-secondary" style={{ cursor: 'pointer' }}>
        {title} ({Object.keys(contracts).length})
      </summary>
      <ul className="list-unstyled small font-monospace mb-0 mt-1">
        {Object.entries(contracts).map(([name, contract]) => (
          <li key={name}>
            {name}({contract.params.join(', ')}) → {contract.returns}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function DevHubConnectionPanel({ hub, connection }: DevHubConnectionPanelProps) {
  const raw = hub?.connection;

  return (
    <section>
      <h2 className="h5">Connection</h2>

      <div className="small">
        <Row label="Tracked state">
          <span className={`badge ${STATUS_BADGE[connection.status]}`}>{connection.status}</span>
          {connection.status === 'disconnected' && (
            <span className="ms-2">
              down for {formatElapsed(connection.disconnectedSince)}, since {connection.disconnectedSince.toISOString()}
            </span>
          )}
        </Row>
        <Row label="SignalR state">{raw?.state ?? '—'}</Row>
        <Row label="Connection id">{raw?.connectionId ?? '—'}</Row>
        <Row label="Hub url">{raw?.baseUrl ?? '—'}</Row>
        <Row label="Hub name">{hub?.hubName ?? '—'}</Row>
        <Row label="Keep alive interval">
          {raw?.keepAliveIntervalInMilliseconds != null ? `${raw.keepAliveIntervalInMilliseconds} ms` : '—'}
        </Row>
        <Row label="Server timeout">
          {raw?.serverTimeoutInMilliseconds != null ? `${raw.serverTimeoutInMilliseconds} ms` : '—'}
        </Row>
      </div>

      <ContractTable title="Client events the hub may send" contracts={PORTAL_HUB_CLIENT_EVENTS} />
      <ContractTable title="Server methods the portal may call" contracts={PORTAL_HUB_SERVER_METHODS} />
    </section>
  );
}
